import { isNullOrUndefined } from './helpers'
import { likertResponseToPercentage } from './statistics'

export const getResultProgressTotal = (results) => {
  return results.reduce(
    (accumulator, currentValue) => accumulator + Math.abs(currentValue.resultProgressSincePrevious),
    0,
  );
}

export const getPreviousSurveyId = (surveySequence, currentSurveyId) => {
  const idx = surveySequence.findIndex(s => s.id === currentSurveyId);
  return idx > 0 ? surveySequence[idx - 1].id : null;
};

export const hasEnoughRespondents = (stats) => stats.__typename === 'SurveyStatisticsOutput';

const progressSince = (current, previous) => (!isNullOrUndefined(current) && !isNullOrUndefined(previous)) ? current - previous : null;

const hasDataInEitherSurvey = (entity) => entity.results[0].result !== null || entity.results[1].result !== null;

export const hasLatestResults = (results) => results.some(r => r.results[0].result !== null);

const statValues = (stat) => ({
  result: stat ? likertResponseToPercentage(stat.aggregatedResponse) : null,
  rate: stat ? stat.aggregatedImportance : null
});

export const getThemesProgressResults = (currentStats, previousStats, questionnaire, currentSurveyId, previousSurveyId) => {
  return questionnaire.catalysts.map(theme => {
    const { result: currentResult } = statValues((currentStats.themes || []).find(t => t.id === theme.id));
    const { result: previousResult } = statValues((previousStats.themes || []).find(t => t.id === theme.id));
    return {
      id: theme.id,
      title: theme.title,
      results: [
        { surveyId: currentSurveyId, result: currentResult },
        { surveyId: previousSurveyId, result: previousResult }
      ],
      resultProgressSincePrevious: progressSince(currentResult, previousResult),
      drivers: theme.drivers.map(driver => {
        const { result: currentDriverResult, rate: currentDriverRate } = statValues((currentStats.drivers || []).find(d => d.id === driver.id));
        const { result: previousDriverResult, rate: previousDriverRate } = statValues((previousStats.drivers || []).find(d => d.id === driver.id));
        return {
          title: driver.title,
          results: [
            { surveyId: currentSurveyId, result: currentDriverResult, rate: currentDriverRate },
            { surveyId: previousSurveyId, result: previousDriverResult, rate: previousDriverRate }
          ],
          resultProgressSincePrevious: progressSince(currentDriverResult, previousDriverResult),
          rateProgressSincePrevious: progressSince(currentDriverRate, previousDriverRate)
        };
      }).filter(hasDataInEitherSurvey)
    };
  }).filter(hasDataInEitherSurvey);
};

export const getThemeProgressResults = (currentStats, previousStats, questionnaire, currentSurveyId, previousSurveyId, catalystId) => {
  const catalyst = questionnaire.catalysts.find(c => c.id === catalystId);
  if (!catalyst) return null;
  const { result: currentResult } = statValues((currentStats.themes || []).find(t => t.id === catalystId));
  const { result: previousResult } = statValues((previousStats.themes || []).find(t => t.id === catalystId));
  return {
    id: catalyst.id,
    title: catalyst.title,
    description: catalyst.description,
    results: [
      { surveyId: currentSurveyId, result: currentResult },
      { surveyId: previousSurveyId, result: previousResult }
    ],
    drivers: getDriversProgressResults(
      currentStats, previousStats, { catalysts: [catalyst] }, currentSurveyId, previousSurveyId
    )
  };
};

export const getDriversProgressResults = (currentStats, previousStats, questionnaire, currentSurveyId, previousSurveyId) => {
  return questionnaire.catalysts.flatMap(catalyst =>
    catalyst.drivers.map(driver => {
      const { result: currentResult, rate: currentRate } = statValues((currentStats.drivers || []).find(d => d.id === driver.id));
      const { result: previousResult, rate: previousRate } = statValues((previousStats.drivers || []).find(d => d.id === driver.id));
      const questionsStatistics = catalyst.questions.filter(q => driver.affectingQuestionIds.includes(q.id)).map(question => {
        const currentQuestion = (currentStats.questions || []).find(q => q.id === question.id);
        const previousQuestion = (previousStats.questions || []).find(q => q.id === question.id);
        const { result: currentQuestionResult, rate: currentQuestionRate } = statValues(currentQuestion);
        const { result: previousQuestionResult, rate: previousQuestionRate } = statValues(previousQuestion);
        return {
          id: question.id,
          title: question.title,
          results: [
            {
              surveyId: currentSurveyId,
              result: currentQuestionResult,
              rate: currentQuestionRate,
              participants: currentQuestion ? currentQuestion.totalRespondentCount : null,
              skipped: currentQuestion ? currentQuestion.skippedRespondentCount : null
            },
            {
              surveyId: previousSurveyId,
              result: previousQuestionResult,
              rate: previousQuestionRate,
              participants: previousQuestion ? previousQuestion.totalRespondentCount : null,
              skipped: previousQuestion ? previousQuestion.skippedRespondentCount : null
            }
          ],
          resultProgressSincePrevious: progressSince(currentQuestionResult, previousQuestionResult),
          rateProgressSincePrevious: progressSince(currentQuestionRate, previousQuestionRate)
        };
      }).filter(hasDataInEitherSurvey);
      return {
        title: driver.title,
        description: driver.description,
        catalystTitle: catalyst.title,
        results: [
          { surveyId: currentSurveyId, result: currentResult, rate: currentRate },
          { surveyId: previousSurveyId, result: previousResult, rate: previousRate }
        ],
        resultProgressSincePrevious: progressSince(currentResult, previousResult),
        rateProgressSincePrevious: progressSince(currentRate, previousRate),
        questionsStatistics
      };
    }).filter(hasDataInEitherSurvey)
  );
};

export const getStatementsProgressResults = (currentStats, previousStats, questionnaire, currentSurveyId, previousSurveyId) => {
  return questionnaire.catalysts.flatMap(catalyst =>
    catalyst.questions.map(question => {
      const { result: currentResult, rate: currentRate } = statValues((currentStats.questions || []).find(q => q.id === question.id));
      const { result: previousResult, rate: previousRate } = statValues((previousStats.questions || []).find(q => q.id === question.id));
      return {
        id: question.id,
        title: question.title,
        catalystTitle: catalyst.title,
        results: [
          { surveyId: currentSurveyId, result: currentResult, rate: currentRate },
          { surveyId: previousSurveyId, result: previousResult, rate: previousRate }
        ],
        resultProgressSincePrevious: progressSince(currentResult, previousResult),
        rateProgressSincePrevious: progressSince(currentRate, previousRate)
      };
    }).filter(hasDataInEitherSurvey)
  );
};



// WEBPACK FOOTER //
// ./src/utilities/progress-reports.js